<template>
  <!--
    Headless: this component owns the rrweb recorder lifecycle but renders
    nothing of its own. Recording consent is given up-front on the `/challenge`
    landing page (a required checkbox), so there is no in-app gate here — the
    recorder just persists that consent and starts.
  -->
  <div v-if="isChallengeActive" data-cy="challenge-recorder" :data-state="state" />
</template>

<script setup lang="ts">
import { logger } from '@src/lib/logger';
import { challengeQueries } from '@therame/api-client';
import { onMounted, ref } from 'vue';
import {
  challengeConsentAcceptedKey,
  useChallengeRecorder,
} from '@/composables/useChallengeRecorder';

const recorder = useChallengeRecorder();

const isChallengeActive = window.localStorage.getItem('challenge_active') === 'true';
const sessionId = window.localStorage.getItem('challenge_session_id');
const apiRoot = import.meta.env.VITE_API_ROOT as string;

type RecorderState = 'idle' | 'recording';
const state = ref<RecorderState>('idle');

const startRecording = () => {
  if (!sessionId) return;
  recorder.start({ sessionId, apiRoot });
  state.value = 'recording';
};

onMounted(async () => {
  if (!isChallengeActive || !sessionId) return;

  // Consent was given on the `/challenge` landing page (the start button is
  // gated on the required checkbox), so we stamp it on the backend and start
  // recording without prompting again. The backend's `consent_at` remains the
  // source of truth; the localStorage marker is a UX nicety to avoid re-POSTing
  // `/consent` on internal navigations. It is scoped to the session id so a
  // stale marker from an earlier challenge cannot suppress the stamp for a new
  // session (which would leave it without consent and 403 every replay batch).
  if (window.localStorage.getItem(challengeConsentAcceptedKey) === sessionId) {
    startRecording();
    return;
  }

  try {
    await challengeQueries.acceptConsent(sessionId);
    startRecording();
  } catch (err) {
    logger.error({ msg: 'Challenge consent acceptance failed', error: err });
    state.value = 'idle';
  }
});
</script>
